import {observable, action, runInAction} from 'mobx';
import tsDate from '@techsee/techsee-date';
import {getRootStore} from '../../app.bootstrap';
import {ResourceType, IResource} from '@techsee/techsee-client-infra/lib/services/HistoryService';

export interface ITagSearchHistoryPartials {
    length: number;
    keys: string[];
    [roomId: string]: ISearchedResource[] | number | string[];
}

export interface ITagSearchResults {
    history: ITagSearchHistoryPartials;
    library: ISearchedResource[];
}

export interface ISearchedResource {
    _id: string;
    url: string;
    isVideo?: boolean;
    storageType?: string;
    tags?: {text: string}[];
    security?: {
        enabled: boolean;
        expires: string;
    };
    [key: string]: any;
}

export interface ISelectedResourceResult {
    resource: ISearchedResource;
    room: {_id?: string};
}

export class SearchResultsDialogController {
    @observable isVisible = false;

    @observable activeTabIndex = 0;

    searchSettings: any = null;
    tagSearchResults: ITagSearchResults | null = null;

    private _resolve?: (data: ISelectedResourceResult | null) => void;

    get historyTabTitle(): string {
        const t = getRootStore().localizationService.translate;
        return t('SEARCH_TAGS.MODAL.VIEW.HISTORY_RESULTS', {
            number: this.tagSearchResults?.history?.length || 0
        });
    }

    get libraryTabTitle(): string {
        const t = getRootStore().localizationService.translate;
        return t('SEARCH_TAGS.MODAL.VIEW.LIBRARY_RESULTS', {
            number: this.tagSearchResults?.library?.length || 0
        });
    }

    get tabsConfig() {
        const tabs = [];

        // History Tab
        if (this.searchSettings?.searchHistoryTags) {
            tabs.push({
                title: this.historyTabTitle,
                type: 'HISTORY'
            });
        }

        if (this.searchSettings?.searchLibraryTags) {
            tabs.push({
                title: this.libraryTabTitle,
                type: 'LIBRARY'
            });
        }

        return tabs;
    }

    get initialTabIndex(): number {
        if (!this.tagSearchResults) return 0;

        const historyLen = this.tagSearchResults.history?.length ?? 0;
        const libraryLen = this.tagSearchResults.library?.length ?? 0;

        const shouldShowHistoryFirst = historyLen >= libraryLen;

        const historyTabExists = this.searchSettings?.searchHistoryTags;
        const libraryTabExists = this.searchSettings?.searchLibraryTags;

        if (shouldShowHistoryFirst && historyTabExists) {
            return 0;
        } else if (!shouldShowHistoryFirst && libraryTabExists) {
            return historyTabExists ? 1 : 0;
        }

        return 0;
    }

    open(searchSettings: any, tagSearchResults: ITagSearchResults): Promise<ISelectedResourceResult | null> {
        runInAction(() => {
            this.searchSettings = searchSettings;
            this.tagSearchResults = tagSearchResults;
            this.isVisible = true;
        });

        return new Promise((resolve) => {
            this._resolve = resolve;
            this.activeTabIndex = this.initialTabIndex;
            this._checkForExpiredResources();
        });
    }

    @action close = () => {
        this.isVisible = false;
        this._resolve?.(null);
        this._resolve = undefined;
    };

    @action confirm(resource: ISearchedResource, room: {_id?: string}) {
        this.isVisible = false;
        this._resolve?.({resource, room});
        this._resolve = undefined;
    }

    @action setActiveTab = (tabIndex: number) => {
        this.activeTabIndex = tabIndex;
    };

    private async _checkForExpiredResources() {
        if (!this.tagSearchResults) return;

        const {history, library} = this.tagSearchResults;

        const promises = [...this._checkExpiredHistory(history), ...this._checkExpiredLibrary(library)];

        await Promise.all(promises);
    }

    private _checkExpiredHistory(history: ITagSearchHistoryPartials) {
        const tasks: Promise<any>[] = [];
        const historyApiService = getRootStore().historyApiService;

        history.keys.forEach((roomId) => {
            const list = history[roomId] as ISearchedResource[];

            list.forEach((resource) => {
                if (this._isExpired(resource)) {
                    const resourceType: ResourceType = resource.isVideo ? 'VIDEO' : 'IMAGE';

                    tasks.push(
                        historyApiService.getResourceById(resource._id, resourceType).then((response: IResource) => {
                            resource.url = response.url;
                            resource.security = response.security as ISearchedResource['security'];
                        })
                    );
                }
            });
        });

        return tasks;
    }

    private _checkExpiredLibrary(library: ISearchedResource[]) {
        const {resourcesService} = getRootStore();

        return library
            .filter((res) => this._isExpired(res))
            .map((resource) =>
                resourcesService.find(resource._id).then((updated) => {
                    resource.url = updated.url;
                    (resource as any).security = updated.security;
                })
            );
    }

    private _isExpired(resource: ISearchedResource) {
        return resource?.security?.enabled && tsDate.utc().isAfter(resource.security.expires);
    }

    selectResource = (resource: ISearchedResource, roomId?: string) => {
        const resourceType: ResourceType = resource.isVideo ? 'VIDEO' : 'IMAGE';
        const {historyApiService, resourcesService} = getRootStore();

        const resourcePromise: Promise<{url: string; security: any}> = roomId
            ? historyApiService
                  .resignResource(resource._id, resourceType)
                  .then(() => historyApiService.getResourceById(resource._id, resourceType))
                  .then((response: IResource) => ({url: response.url, security: response.security}))
            : resourcesService.resign(resource._id).then((resp) => ({
                  url: resp.url,
                  security: resp.security
              }));

        resourcePromise
            .then(({url, security}) => {
                resource.url = url;
                resource.security = security;
                this.confirm(resource, {_id: roomId});
            })
            .catch((error) => {
                console.error('Failed to resign resource:', error);
                this.close();
            });
    };
}
